import { usePageMeta } from '@/hooks/usePageMeta';
import SectionHeading from '@/components/base/SectionHeading';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/comics';

const principles = [
  {
    icon: 'ri-focus-3-line',
    title: '题材为先',
    desc: '一切以题材为线索组织内容，奇幻、校园、恋爱等热门分类清晰直达，让你按兴趣快速找到想看的韩漫。',
  },
  {
    icon: 'ri-list-check-2',
    title: '分类严谨',
    desc: '每个作品都尽量匹配准确的题材标签，避免无效堆叠，确保筛选结果真正贴近你的阅读偏好。',
  },
  {
    icon: 'ri-leaf-line',
    title: '轻快体验',
    desc: '精简页面结构、优化图片与脚本加载，让手机、平板和电脑都能获得流畅不卡顿的浏览体验。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新',
    desc: '连载作品信息持续跟进，首页与分类页实时反映最新章节与更新时间，方便你安心追更。',
  },
];

const testimonials = [
  {
    name: '小鹿酱',
    role: '奇幻漫画爱好者',
    avatar: '鹿',
    quote: '之前找奇幻韩漫要翻好几个平台，现在在分类页点开就能看，异世界题材一搜一大把，省了不少时间。',
  },
  {
    name: '阿泽',
    role: '校园题材老读者',
    avatar: '泽',
    quote: '题材标签分得细，社团、青春、治愈都能直接筛，页面也轻快，用手机刷很舒服。',
  },
  {
    name: '林lin',
    role: '恋爱漫资深粉',
    avatar: '林',
    quote: '双向暗恋和甜宠标签太懂我了，热门推荐也很对味，点开即看真的很方便。',
  },
];

const values = [
  { icon: 'ri-compass-3-line', title: '以读者为中心', desc: '每一次分类和筛选都围绕读者找漫画的真实需求展开。' },
  { icon: 'ri-heart-line', title: '尊重内容创作', desc: '明确标注信息仅供推荐参考，尊重作品版权与阅读秩序。' },
  { icon: 'ri-line-chart-line', title: '持续做减法', desc: '不断去掉冗余，让找漫画这件事变得更简单、更轻快。' },
];

export default function About() {
  usePageMeta({
    title: '关于我们 - 韩漫漫画网',
    description:
      '了解韩漫漫画网：以题材聚合为核心的韩漫分类平台。覆盖奇幻、校园、恋爱等热门题材，分类直达、更新及时、多端适配，帮读者快速找到想看的韩漫。',
    keywords: '韩漫漫画网,关于我们,韩漫题材分类,韩漫聚合平台',
    canonical: 'https://lovecomix.github.io/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于韩漫漫画网',
        url: 'https://lovecomix.github.io/about',
        inLanguage: 'zh-CN',
        about: {
          '@type': 'Organization',
          name: '韩漫漫画网',
          description: '聚合奇幻、校园、恋爱等热门题材韩漫的分类直达平台',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '韩漫漫画网',
        url: 'https://lovecomix.github.io/',
        description: '聚合奇幻、校园、恋爱等热门题材韩漫的分类直达平台',
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50 pt-24">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-6 max-w-3xl">
            <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">关于韩漫漫画网</h1>
            <p className="mt-4 leading-relaxed text-foreground-600">
              韩漫漫画网是一个以题材聚合为核心的韩漫分类平台。我们相信，找漫画不该是件费劲的事——奇幻、校园、恋爱等热门题材被清晰地聚在一起，分类直达、点开即看，让你把时间留给真正想看的作品。
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <SectionHeading eyebrow="内容原则" title="我们坚持的四件事" align="left" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {principles.map((p) => (
            <div
              key={p.title}
              className="rounded-2xl bg-background-100 p-6 ring-1 ring-background-200/70 transition-all hover:-translate-y-1 hover:ring-primary-300"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-xl text-primary-700">
                <i className={p.icon} />
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <SectionHeading eyebrow="发展理念" title="把找漫画这件事变简单" />
          <div className="grid gap-5 md:grid-cols-3">
            {values.map((v, idx) => (
              <div key={v.title} className="relative rounded-2xl bg-background-50 p-6 ring-1 ring-background-200/70">
                <span className="absolute right-5 top-5 font-heading text-4xl text-background-200">
                  0{idx + 1}
                </span>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary-100 text-xl text-secondary-700">
                  <i className={v.icon} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <SectionHeading eyebrow="用户口碑" title="读者怎么说" desc="来自韩漫读者们最真实的使用感受。" />
        <div className="grid gap-5 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="flex flex-col rounded-2xl bg-background-100 p-6 ring-1 ring-background-200/70">
              <i className="ri-double-quotes-l text-3xl text-primary-300" />
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                {t.quote}
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-sm font-medium text-background-50">
                  {t.avatar}
                </span>
                <div>
                  <div className="text-sm font-semibold text-foreground-950">{t.name}</div>
                  <div className="text-xs text-foreground-500">{t.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <SectionHeading eyebrow="联系我们" title="随时欢迎交流" />
          <div className="mx-auto grid max-w-3xl gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-4 rounded-2xl bg-background-50 p-5 ring-1 ring-background-200/70">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-xl text-primary-700">
                <i className="ri-mail-line" />
              </span>
              <div>
                <div className="text-sm font-semibold text-foreground-950">内容建议</div>
                <div className="mt-0.5 text-sm text-foreground-600">hello@hanman.example.com</div>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-2xl bg-background-50 p-5 ring-1 ring-background-200/70">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-xl text-accent-700">
                <i className="ri-wechat-line" />
              </span>
              <div>
                <div className="text-sm font-semibold text-foreground-950">意见反馈</div>
                <div className="mt-0.5 text-sm text-foreground-600">欢迎通过站内常见问题了解详情</div>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-10 max-w-3xl">
            <h3 className="mb-4 text-center text-base font-semibold text-foreground-950">本站题材分类</h3>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={`/${c.key}`}
                  className="rounded-full bg-background-50 px-4 py-2 text-sm text-primary-600 ring-1 ring-primary-200 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  {c.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}